import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DateTimePicker from "../components/ui/DateTimePicker";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";

const emptyForm = {
  sailingId: "",
  vesselId: "",
  route: "",
  departurePort: "",
  arrivalPort: "",
  etd: "",
  eta: "",
  bookingCutoff: "",
  status: "Draft",
  freightCapacity: "",
  livestockCapacity: "",
  weightLimit: "",
  zones: [],
};

export default function EditSailing() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingVessels, setLoadingVessels] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadPageData();
  }, [id]);

  async function loadPageData() {
    try {
      setLoading(true);
      setLoadingVessels(true);

      const [sailingRes, vesselsRes] = await Promise.all([
        fetch(`${API_BASE}/sailings/${id}`),
        fetch(`${API_BASE}/vessels`),
      ]);

      if (!sailingRes.ok) {
        throw new Error("Could not load sailing.");
      }

      const sailingData = await sailingRes.json();
      const vesselsData = vesselsRes.ok ? await vesselsRes.json() : [];

      const vesselList = Array.isArray(vesselsData)
        ? vesselsData
        : Array.isArray(vesselsData?.data)
        ? vesselsData.data
        : [];

      setVessels(vesselList);

      const sailing = sailingData?.data || sailingData;

      setForm({
        sailingId: sailing.sailingRef || sailing.sailingId || "",
        vesselId:
          sailing.vessel?._id ||
          sailing.vessel ||
          sailing.vesselId ||
          "",
        route: sailing.routeLabel || sailing.route || "",
        departurePort:
          sailing.origin ||
          sailing.departurePort ||
          "",
        arrivalPort:
          sailing.destination ||
          sailing.arrivalPort ||
          "",
        etd: toDateTimeLocal(sailing.etd),
        eta: toDateTimeLocal(sailing.eta),
        bookingCutoff: toDateTimeLocal(sailing.bookingCutoff),
        status: toTitleCase(sailing.status || "Draft"),
        freightCapacity:
          sailing.capacity?.maxCubeM3 ??
          sailing.freightCapacity ??
          "",
        livestockCapacity:
          sailing.capacity?.maxLivestockHead ??
          sailing.livestockCapacity ??
          "",
        weightLimit:
          sailing.capacity?.maxWeightKg ??
          sailing.weightLimit ??
          "",
        zones:
          sailing.zones ||
          sailing.capacityZones ||
          [],
      });
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Could not load sailing.");
    } finally {
      setLoading(false);
      setLoadingVessels(false);
    }
  }

  const selectedVessel = useMemo(() => {
    return vessels.find((v) => String(v._id || v.id) === String(form.vesselId));
  }, [vessels, form.vesselId]);

  function handleChange(e) {
    const { name, value } = e.target;

    if (name === "vesselId") {
      const vessel = vessels.find((v) => String(v._id || v.id) === String(value));

      setForm((prev) => ({
        ...prev,
        vesselId: value,
        freightCapacity:
          vessel?.freightCapacity ??
          vessel?.capacity?.freight ??
          vessel?.freightLimit ??
          prev.freightCapacity,
        livestockCapacity:
          vessel?.livestockCapacity ??
          vessel?.capacity?.livestock ??
          vessel?.livestockLimit ??
          prev.livestockCapacity,
        weightLimit:
          vessel?.weightLimit ??
          vessel?.capacity?.weight ??
          vessel?.maxWeightKg ??
          prev.weightLimit,
        zones: vessel?.zones || vessel?.capacityZones || prev.zones || [],
      }));

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function buildRoute() {
    if (!form.departurePort || !form.arrivalPort) return form.route;
    return `${form.departurePort} → ${form.arrivalPort}`;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage("");

    if (!form.sailingId.trim()) {
      setMessage("Add a Sailing ID.");
      return;
    }

    if (!form.vesselId) {
      setMessage("Select a vessel.");
      return;
    }

    try {
      setSaving(true);

      // The DateTimePicker gives back a plain "YYYY-MM-DDTHH:mm" string with
      // no timezone marker — sent as-is, the backend (running in a
      // different timezone, typically UTC) parses that same string as its
      // OWN local time, silently shifting the actual moment by whatever the
      // offset between the two is (e.g. a 9pm NZ selection landing as 9pm
      // UTC, which redisplays as ~9am NZ the next day). Converting to a
      // proper ISO string with an explicit UTC offset here removes the
      // ambiguity — `new Date(localString)` correctly interprets the
      // string in the BROWSER's timezone, and toISOString() gives the one
      // unambiguous representation of that same moment.
      const toUtcIso = (localDateTimeStr) => {
        if (!localDateTimeStr) return null;
        const d = new Date(localDateTimeStr);
        return Number.isNaN(d.getTime()) ? null : d.toISOString();
      };

      const payload = {
        sailingRef: form.sailingId,
        vessel: form.vesselId,
        origin: form.departurePort,
        destination: form.arrivalPort,
        routeLabel: buildRoute(),
        etd: toUtcIso(form.etd),
        eta: toUtcIso(form.eta),
        bookingCutoff: toUtcIso(form.bookingCutoff),
        status: String(form.status || "draft").toLowerCase(),
        capacity: {
          maxWeightKg: Number(form.weightLimit) || 0,
          maxCubeM3: Number(form.freightCapacity) || 0,
          maxLivestockHead: Number(form.livestockCapacity) || 0,
        },
        zones: form.zones || [],
      };

      const res = await fetch(`${API_BASE}/sailings/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Failed to update sailing");
      }

      setMessage("Sailing updated successfully.");

      setTimeout(() => {
        navigate(`/dashboard/sailings/${id}`);
      }, 500);
    } catch (err) {
      console.error(err);
      setMessage(`Could not update sailing: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  const messageIsError =
    message &&
    (message.toLowerCase().includes("could not") ||
      message.toLowerCase().includes("failed") ||
      message.toLowerCase().includes("select") ||
      message.toLowerCase().includes("add"));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-600">Loading sailing...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mb-4 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              ← Back
            </button>

            <h1 className="text-3xl font-bold tracking-tight text-slate-950">
              Edit Sailing
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Update this sailing schedule. Existing details are pulled through automatically.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/dashboard/sailings/${id}`)}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              form="edit-sailing-form"
              disabled={saving}
              className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? "Updating..." : "Update Sailing"}
            </button>
          </div>
        </div>

        {message ? (
          <div
            className={`mb-6 rounded-2xl border px-4 py-3 text-sm shadow-sm ${
              messageIsError
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}
          >
            {message}
          </div>
        ) : null}

        <form id="edit-sailing-form" onSubmit={handleSubmit}>
          <div className="grid gap-6 xl:grid-cols-[1.75fr_0.85fr]">
            <div className="space-y-6">
              <SectionCard
                title="Sailing Details"
                description="Core sailing info, vessel assignment, route, and schedule."
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                  <Field label="Sailing ID">
                    <input
                      name="sailingId"
                      value={form.sailingId}
                      onChange={handleChange}
                      placeholder="e.g. SS-2026-001"
                      className="input"
                    />
                  </Field>

                  <Field label="Vessel">
                    <select
                      name="vesselId"
                      value={form.vesselId}
                      onChange={handleChange}
                      className="input"
                    >
                      <option value="">
                        {loadingVessels ? "Loading vessels..." : "Select Vessel"}
                      </option>
                      {vessels.map((v) => (
                        <option key={v._id || v.id} value={v._id || v.id}>
                          {v.name || v.vesselName || "Unnamed Vessel"}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Status">
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                      className="input"
                    >
                      <option>Draft</option>
                      <option>Open</option>
                      <option>Closed</option>
                      <option>Departed</option>
                      <option>Arrived</option>
                      <option>Cancelled</option>
                    </select>
                  </Field>

                  <Field label="Departure Port">
                    <input
                      name="departurePort"
                      value={form.departurePort}
                      onChange={handleChange}
                      placeholder="e.g. Napier"
                      className="input"
                    />
                  </Field>

                  <Field label="Arrival Port">
                    <input
                      name="arrivalPort"
                      value={form.arrivalPort}
                      onChange={handleChange}
                      placeholder="e.g. Chatham Islands"
                      className="input"
                    />
                  </Field>

                  <Field label="Route">
                    <input
                      name="route"
                      value={buildRoute()}
                      onChange={handleChange}
                      placeholder="Auto generated"
                      className="input bg-slate-50 text-slate-500"
                      readOnly
                    />
                  </Field>

                  <Field label="ETD">
                    <DateTimePicker
                      value={form.etd}
                      onChange={(val) => setForm((p) => ({ ...p, etd: val }))}
                    />
                  </Field>

                  <Field label="ETA">
                    <DateTimePicker
                      value={form.eta}
                      onChange={(val) => setForm((p) => ({ ...p, eta: val }))}
                    />
                  </Field>

                  <Field label="Booking Cutoff">
                    <DateTimePicker
                      value={form.bookingCutoff}
                      onChange={(val) => setForm((p) => ({ ...p, bookingCutoff: val }))}
                    />
                  </Field>
                </div>
              </SectionCard>

              <SectionCard
                title="Capacity Settings"
                description="Capacity values are pulled from the selected vessel and can be adjusted."
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                  <Field label="Freight Capacity">
                    <input
                      name="freightCapacity"
                      type="number"
                      value={form.freightCapacity}
                      onChange={handleChange}
                      placeholder="e.g. 40"
                      className="input"
                    />
                  </Field>

                  <Field label="Livestock Capacity">
                    <input
                      name="livestockCapacity"
                      type="number"
                      value={form.livestockCapacity}
                      onChange={handleChange}
                      placeholder="e.g. 1200"
                      className="input"
                    />
                  </Field>

                  <Field label="Weight Limit KG">
                    <input
                      name="weightLimit"
                      type="number"
                      value={form.weightLimit}
                      onChange={handleChange}
                      placeholder="e.g. 250000"
                      className="input"
                    />
                  </Field>
                </div>
              </SectionCard>

              <SectionCard
                title="Zone Availability"
                description="Preview configured vessel zones for this sailing."
              >
                {!selectedVessel ? (
                  <EmptyState text="Select a vessel to preview zones." />
                ) : form.zones.length === 0 ? (
                  <EmptyState text="This vessel has no zones configured yet." />
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {form.zones.map((zone, index) => (
                      <div
                        key={zone._id || zone.id || index}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <p className="font-semibold text-slate-900">
                          {zone.name || zone.zoneName || `Zone ${index + 1}`}
                        </p>
                        <div className="mt-3 space-y-1 text-sm text-slate-600">
                          <p>Type: {zone.type || "General"}</p>
                          <p>Capacity: {zone.capacity || zone.limit || "Not set"}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>

            <div className="space-y-6">
              <AsideCard title="Summary">
                <SummaryRow label="Sailing ID" value={form.sailingId || "—"} />
                <SummaryRow
                  label="Vessel"
                  value={
                    selectedVessel?.name ||
                    selectedVessel?.vesselName ||
                    "Not selected"
                  }
                />
                <SummaryRow label="Route" value={buildRoute() || "—"} />
                <SummaryRow label="Status" value={form.status || "Draft"} />
                <SummaryRow label="ETD" value={form.etd || "—"} />
                <SummaryRow label="ETA" value={form.eta || "—"} />
                <SummaryRow
                  label="Booking Cutoff"
                  value={form.bookingCutoff || "—"}
                />
              </AsideCard>

              <AsideCard title="Capacity Overview">
                <SummaryRow
                  label="Freight Capacity"
                  value={form.freightCapacity || "—"}
                />
                <SummaryRow
                  label="Livestock Capacity"
                  value={form.livestockCapacity || "—"}
                />
                <SummaryRow
                  label="Weight Limit KG"
                  value={form.weightLimit || "—"}
                />
                <SummaryRow
                  label="Zones"
                  value={String(form.zones?.length || 0)}
                />
              </AsideCard>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.9rem;
          border: 1px solid rgb(203 213 225);
          background: white;
          padding: 0.75rem 0.95rem;
          font-size: 0.95rem;
          color: rgb(15 23 42);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .input::placeholder {
          color: rgb(148 163 184);
        }

        .input:hover {
          border-color: rgb(148 163 184);
        }

        .input:focus {
          border-color: rgb(15 23 42);
          box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.08);
        }
      `}</style>
    </div>
  );
}

function toDateTimeLocal(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);

  return localDate.toISOString().slice(0, 16);
}

function toTitleCase(value) {
  if (!value) return "Draft";

  return String(value)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function SectionCard({ title, description, children }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function AsideCard({ title, children }) {
  return (
    <aside className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
        {title}
      </h2>
      <div className="mt-4 space-y-3">{children}</div>
    </aside>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span className="max-w-[60%] break-words text-right text-sm font-semibold text-slate-900">
        {value}
      </span>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
      {text}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-slate-700">
        {label}
      </span>
      {children}
    </label>
  );
}